import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { contractsByUser } from "../Redux/products/slice";
import UpdateContractModal from "./UpdateContract";
import { PropertyContractTableConfig } from "../components/mockData";
import { FormattedMessage, useIntl } from 'react-intl';
import {
  createStyles,
  Table,
  ScrollArea,
  UnstyledButton,
  Group,
  Text,
  TextInput,
  Tabs,
  Paper,
  Loader,
  Center,
  Badge,
  ActionIcon,
  Tooltip,
  Box,
  Stack,
  Card,
  rem,
  Collapse,
  Button,
} from '@mantine/core';
import { IconSelector, IconChevronDown, IconChevronUp, IconSearch, IconEdit, IconEye, IconShoppingCart, IconPackage, IconCheck, IconX, IconFileDescription } from '@tabler/icons-react';
import Dropdown from "../components/Dropdown";

// Styles for the component
const useStyles = createStyles((theme) => ({
  header: {
    position: 'sticky',
    top: 0,
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
    transition: 'box-shadow 150ms ease',
    zIndex: 1,

    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      borderBottom: `1px solid ${
        theme.colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[2]
      }`,
    },
  },

  th: {
    padding: '0 !important',
  },

  control: {
    width: '100%',
    padding: `${theme.spacing.xs} ${theme.spacing.md}`,

    '&:hover': {
      backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
    },
  },

  icon: {
    width: rem(21),
    height: rem(21),
    borderRadius: rem(21),
  },

  tableContainer: {
    borderRadius: theme.radius.md,
    boxShadow: theme.shadows.xs,
  },

  contractCard: {
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
    transition: 'transform 150ms ease, box-shadow 150ms ease',
    marginBottom: theme.spacing.md,

    '&:hover': {
      transform: 'scale(1.01)',
      boxShadow: theme.shadows.md,
    },
  },

  draftBadge: {
    backgroundColor: theme.colors.blue[1],
    color: theme.colors.blue[7],
  },

  buyBadge: {
    backgroundColor: theme.colors.green[1],
    color: theme.colors.green[7],
  },

  sellBadge: {
    backgroundColor: theme.colors.orange[1],
    color: theme.colors.orange[7],
  },

  completedBadge: {
    backgroundColor: theme.colors.teal[1],
    color: theme.colors.teal[7],
  },

  voidBadge: {
    backgroundColor: theme.colors.gray[1],
    color: theme.colors.gray[7],
  },

  noData: {
    textAlign: 'center',
    padding: theme.spacing.xl,
    color: theme.colors.gray[6],
  },
}));

// Table header component
function Th({ children, reversed, sorted, onSort }) {
  const { classes } = useStyles();
  const Icon = sorted ? (reversed ? IconChevronUp : IconChevronDown) : IconSelector;
  return (
    <th className={classes.th}>
      <UnstyledButton onClick={onSort} className={classes.control}>
        <Group position="apart">
          <Text fw={500} fz="sm">{children}</Text>
          {sorted && <Icon size="0.9rem" stroke={1.5} />}
        </Group>
      </UnstyledButton>
    </th>
  );
}

// Data sorting function
function sortData(data, payload) {
  const { sortBy } = payload;

  if (!data || !data.length) return [];
  
  let sortedData = [...data];

  if (sortBy) {
    sortedData.sort((a, b) => {
      const valueA = typeof a[sortBy] === 'number'
        ? a[sortBy]
        : (a[sortBy] || '').toString().toLowerCase();

      const valueB = typeof b[sortBy] === 'number'
        ? b[sortBy]
        : (b[sortBy] || '').toString().toLowerCase();

      if (payload.reversed) {
        return valueB > valueA ? 1 : valueB < valueA ? -1 : 0;
      }

      return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
    });
  }

  return filterData(sortedData, payload.search);
}

// Data filtering function
function filterData(data, search) {
  if (!search || !search.trim()) return data;
  
  const query = search.toLowerCase().trim();
  return data.filter((item) =>
    Object.entries(item).some(([key, value]) => {
      // Skip properties that are objects or arrays
      if (
        value === null || 
        value === undefined || 
        typeof value === 'object' || 
        Array.isArray(value)
      ) {
        return false;
      }
      
      // Convert value to string and check if it includes the search query
      return value.toString().toLowerCase().includes(query);
    })
  );
}

// Contract table component
function ContractTable({ data, onActionSelect }) {
  const { classes } = useStyles();
  const [sortBy, setSortBy] = useState(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [search, setSearch] = useState('');
  const [sortedData, setSortedData] = useState(data || []);
  const intl = useIntl();

  useEffect(() => {
    if (data) {
      const sorted = sortData(data, { sortBy, reversed: reverseSortDirection, search });
      setSortedData(sorted);
    }
  }, [data, sortBy, reverseSortDirection, search]);

  const handleSearchChange = (event) => {
    const { value } = event.currentTarget;
    setSearch(value);
  };

  const setSorting = (field) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
  };

  const actions = [
    { label: <FormattedMessage id="action.operate" />, value: 'view', icon: <IconEye size="1rem" /> },
    { label: <FormattedMessage id="action.negotiate" />, value: 'update', icon: <IconEdit size="1rem" /> },
  ];

  const handleActionChange = (value, rowData) => {
    onActionSelect(value, rowData);
  };

  const rows = sortedData.map((row, index) => (
    <tr key={`${row.productRefID}-${row.SellerID}-${index}`}>
      {PropertyContractTableConfig.map((column, colIndex) => {
        let cellValue = '';
                  
        if (column.accessor === 'period' && row[column.accessor] && row[column.accessor].includes(' - ')) {
          const splitValues = row[column.accessor].split(' - ');
          cellValue = splitValues[0].substring(0, 10) + ' - ' + splitValues[1].substring(0, 10);
        } else {
          cellValue = row[column.accessor] || column.defaultValue || '';
        }
                  
        // Check if productID is 99999 or -1
        if (column.accessor === 'sku' && (row[column.accessor] === 99999 || row[column.accessor] === -1)) {
          cellValue = 'Draft';
        }
                  
        return (
          <td key={colIndex}>
            {column.link ? (
              <a
                rel="noopener noreferrer"
                href={`https://hashscan.io/testnet/contract/${row[column.accessor]}`}
                target="_blank"
                style={{ color: '#2c5810' }}
              >
                {cellValue}
              </a>
            ) : (
              cellValue
            )}
          </td>
        );
      })}
      <td>
        <Group spacing="xs" position="right">
          <Tooltip label={intl.formatMessage({ id: "action.operate" })}>
            <ActionIcon 
              color="blue" 
              variant="subtle"
              onClick={() => handleActionChange('view', row)}
            >
              <IconEye size="1.125rem" />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={intl.formatMessage({ id: "action.negotiate" })}>
            <ActionIcon 
              color="green" 
              variant="subtle"
              onClick={() => handleActionChange('update', row)}
            >
              <IconEdit size="1.125rem" />
            </ActionIcon>
          </Tooltip>
        </Group>
      </td>
    </tr>
  ));

  return (
    <Paper className={classes.tableContainer}>
      <ScrollArea>
        <TextInput
          placeholder={intl.formatMessage({ id: "myprods.search.placeholder" })}
          mb="md"
          icon={<IconSearch size="0.9rem" stroke={1.5} />}
          value={search}
          onChange={handleSearchChange}
        />
        
        <Table striped highlightOnHover withBorder withColumnBorders>
          <thead className={classes.header}>
            <tr>
              {PropertyContractTableConfig.map((column, index) => (
                <Th
                  key={index}
                  sorted={sortBy === column.accessor}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting(column.accessor)}
                >
                  {column.column}
                </Th>
              ))}
              <th>
                <Text fw={500} fz="sm" ta="center">
                  <FormattedMessage id="myprods.actionsmenu.title" />
                </Text>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows
            ) : (
              <tr>
                <td colSpan={PropertyContractTableConfig.length + 1}>
                  <Text className={classes.noData}>
                    <FormattedMessage id="myprods.nodata" defaultMessage="No data available" />
                  </Text>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </ScrollArea>
    </Paper>
  );
}

function ContractCards({ data, onActionSelect, isGrouped = false, groupField = 'productSKU' }) {
  const { classes } = useStyles();
  const intl = useIntl();
  const [expandedGroups, setExpandedGroups] = useState({});

  // Group contracts by the specified field if isGrouped is true
  const groupedData = isGrouped
    ? data.reduce((acc, contract) => {
        const groupKey = contract[groupField] || 'unknown';
        if (!acc[groupKey]) {
          acc[groupKey] = [];
        }
        acc[groupKey].push(contract);
        return acc;
      }, {})
    : { all: data };

  const toggleGroup = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  // Determine badge color based on contract status
  const getBadgeProps = (contract) => {
    const isBuyer = contract.role === 'buyer';
    const isDraft = contract.sku === 99999 || contract.sku === -1;
    const isCompleted = contract.status === 'completed';
    const isVoid = contract.status === 'void';

    if (isDraft) {
      return { 
        color: 'blue', 
        variant: 'light',
        icon: <IconFileDescription size="0.8rem" />,
        label: intl.formatMessage({ id: "contract.draft" }, { defaultMessage: "Draft" })
      };
    } else if (isCompleted) {
      return { 
        color: 'teal', 
        variant: 'light',
        icon: <IconCheck size="0.8rem" />,
        label: intl.formatMessage({ id: "contract.completed" }, { defaultMessage: "Completed" })
      };
    } else if (isVoid) {
      return { 
        color: 'gray', 
        variant: 'light',
        icon: <IconX size="0.8rem" />,
        label: intl.formatMessage({ id: "contract.void" }, { defaultMessage: "Void" })
      };
    } else if (isBuyer) {
      return { 
        color: 'green', 
        variant: 'light',
        icon: <IconShoppingCart size="0.8rem" />,
        label: intl.formatMessage({ id: "contract.buy" }, { defaultMessage: "Buy" })
      };
    } else {
      return { 
        color: 'orange', 
        variant: 'light',
        icon: <IconPackage size="0.8rem" />,
        label: intl.formatMessage({ id: "contract.sell" }, { defaultMessage: "Sell" })
      };
    }
  };

  // Render each group
  const renderGroups = Object.entries(groupedData).map(([groupKey, contracts]) => {
    // Find draft contract for this group
    const draftContract = contracts.find(c => c.sku === 99999 || c.sku === -1);
    const otherContracts = contracts.filter(c => c.sku !== 99999 && c.sku !== -1);

    // Skip rendering if no contracts in this group
    if (contracts.length === 0) return null;

    return (
      <Card key={groupKey} withBorder shadow="sm" radius="md" mb="md">
        {isGrouped && groupKey !== 'all' && (
          <Group position="apart" mb="xs">
            <Text weight={700}>
              {intl.formatMessage(
                { id: "contract.group" }, 
                { 
                  groupField: groupField.charAt(0).toUpperCase() + groupField.slice(1), 
                  groupValue: groupKey 
                }
              )}
            </Text>
            <Button 
              variant="subtle" 
              compact 
              onClick={() => toggleGroup(groupKey)}
            >
              {expandedGroups[groupKey] ? 'Hide' : 'Show'} ({contracts.length})
            </Button>
          </Group>
        )}
        <Collapse in={!isGrouped || expandedGroups[groupKey] === true}>
          <Stack spacing="md">
            {/* Render draft contract first if it exists */}
            {draftContract && (
              <Card key={`${draftContract.productRefID}-${draftContract.SellerID}-draft`} 
                className={classes.contractCard}
                withBorder
              >
                <Group position="apart">
                  <Badge 
                    size="lg"
                    leftSection={<IconFileDescription size="0.8rem" />}
                    color="blue" 
                    variant="light"
                  >
                    {intl.formatMessage({ id: "myprods.state.draft" }, { defaultMessage: "Draft" })}
                  </Badge>
                  
                  <Group spacing="xs">
                    <Tooltip 
                      label={intl.formatMessage({ id: "action.operate" })}
                      color="white"
                      withArrow
                      position="top"
                      styles={(theme) => ({
                        tooltip: {
                          color: theme.colors.dark[9],
                          fontWeight: 500,
                          fontSize: theme.fontSizes.sm,
                          borderRadius: theme.radius.sm,
                          boxShadow: theme.shadows.sm,
                        }
                      })}
                    >
                      <ActionIcon 
                        color="blue" 
                        variant="subtle"
                        onClick={() => onActionSelect('view', draftContract)}
                      >
                        <IconEye size="1.125rem" />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip 
                      label={intl.formatMessage({ id: "action.negotiate" })}
                      color="white"
                      withArrow
                      position="top"
                      styles={(theme) => ({
                        tooltip: {
                          color: theme.colors.dark[9],
                          fontWeight: 500,
                          fontSize: theme.fontSizes.sm,
                          borderRadius: theme.radius.sm,
                          boxShadow: theme.shadows.sm,
                        }
                      })}
                    >
                      <ActionIcon 
                        color="green" 
                        variant="subtle"
                        onClick={() => onActionSelect('update', draftContract)}
                      >
                        <IconEdit size="1.125rem" />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Group>
                
                <Box mt="md">
                  {/* Contract details */}
                  {PropertyContractTableConfig.filter(col => 
                    ['name', 'price', 'quantity', 'period', 'productName', 'productNotes'].includes(col.accessor)
                  ).map((column, idx) => {
                    let value = '';
                    if (column.accessor === 'period' && draftContract[column.accessor] && draftContract[column.accessor].includes(' - ')) {
                      const splitValues = draftContract[column.accessor].split(' - ');
                      value = splitValues[0].substring(0, 10) + ' - ' + splitValues[1].substring(0, 10);
                    } else {
                      value = draftContract[column.accessor] || column.defaultValue || '';
                    }
                    
                    return (
                      <Group key={idx} position="apart" mb="xs">
                        <Text size="sm" color="dimmed">{column.column}:</Text>
                        <Text size="sm" weight={500}>{value}</Text>
                      </Group>
                    );
                  })}
                </Box>
              </Card>
            )}
            {/* Render other contracts */}
            {otherContracts.map((contract, index) => {
              const badgeProps = getBadgeProps(contract);
              return (
                <Card key={`${contract.productRefID}-${contract.SellerID}-${index}`} 
                  className={classes.contractCard}
                  withBorder
                >
                  <Group position="apart">
                    <Badge 
                      size="lg"
                      leftSection={badgeProps.icon}
                      color={badgeProps.color} 
                      variant={badgeProps.variant}
                    >
                      {badgeProps.label}
                    </Badge>
                    
                    <Group spacing="xs">
                      <Tooltip 
                        label={intl.formatMessage({ id: "action.operate" })}
                        color="white"
                        withArrow
                        position="top"
                        styles={(theme) => ({
                          tooltip: {
                            color: theme.colors.dark[9],
                            fontWeight: 500,
                            fontSize: theme.fontSizes.sm,
                            borderRadius: theme.radius.sm,
                            boxShadow: theme.shadows.sm,
                          }
                        })}
                      >
                        <ActionIcon 
                          color="blue" 
                          variant="subtle"
                          onClick={() => onActionSelect('view', contract)}
                        >
                          <IconEye size="1.125rem" />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip 
                        label={intl.formatMessage({ id: "action.negotiate" })}
                        color="white"
                        withArrow
                        position="top"
                        styles={(theme) => ({
                          tooltip: {
                            color: theme.colors.dark[9],
                            fontWeight: 500,
                            fontSize: theme.fontSizes.sm,
                            borderRadius: theme.radius.sm,
                            boxShadow: theme.shadows.sm,
                          }
                        })}
                      >
                        <ActionIcon 
                          color="green" 
                          variant="subtle"
                          onClick={() => onActionSelect('update', contract)}
                        >
                          <IconEdit size="1.125rem" />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Group>
                  
                  <Box mt="md">
                    {/* Contract details */}
                    {PropertyContractTableConfig.filter(col => 
                      ['name', 'price', 'quantity', 'period', 'sku', 'productName', 'productNotes'].includes(col.accessor)
                    ).map((column, idx) => {
                      let value = '';
                      if (column.accessor === 'period' && contract[column.accessor] && contract[column.accessor].includes(' - ')) {
                        const splitValues = contract[column.accessor].split(' - ');
                        value = splitValues[0].substring(0, 10) + ' - ' + splitValues[1].substring(0, 10);
                      } else {
                        value = contract[column.accessor] || column.defaultValue || '';
                      }
                      
                      return (
                        <Group key={idx} position="apart" mb="xs">
                          <Text size="sm" color="dimmed">{column.column}:</Text>
                          <Text size="sm" weight={500}>
                          {column.link ? (
                            <a
                              rel="noopener noreferrer"
                              href={`https://hashscan.io/testnet/contract/${contract[column.accessor]}`}
                              target="_blank"
                              style={{ color: '#2c5810' }}
                            >
                              {value}
                            </a>
                            ) : (
                            value
                            )}
                          </Text>
                        </Group>
                      );
                    })}
                  </Box>
                </Card>
              );
            })}
          </Stack>
        </Collapse>
      </Card>
    );
  });

  return <>{renderGroups}</>;
}

// Main component
export default function MyContracts() {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.products.userContracts) || [];
  const [open, setOpen] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const intl = useIntl();
  const { classes } = useStyles();

  useEffect(() => {
    setLoading(true);
    dispatch(contractsByUser());
    // Use a timeout to ensure the loading state shows for at least a moment
    // This also avoids the Promise error since we're not trying to use .then() on the dispatch
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);
    
    // Clean up the timer if the component unmounts
    return () => clearTimeout(timer);
  }, [dispatch]);

  const handleActionSelect = (action, rowData) => {
    if (action === 'view') {
      setIsUpdate(false);
      setOpen(true);
      setSelected(rowData);
    } else if (action === 'update') {
      setIsUpdate(true);
      setOpen(true);
      setSelected(rowData);
    }
  };

  const handleModalClose = () => {
    dispatch(contractsByUser());
    setOpen(false);
  };

  // Filter data based on contract types
  const buyContracts = data.filter(contract => 
    contract.role === 'buyer' && 
    contract.status !== 'completed' && 
    contract.status !== 'void' &&
    contract.sku !== 99999 && 
    contract.sku !== -1
  );

  const sellContracts = data.filter(contract => 
    contract.role === 'seller' && 
    contract.status !== 'completed' && 
    contract.status !== 'void' &&
    contract.sku !== 99999 && 
    contract.sku !== -1
  );

  const draftContracts = data.filter(contract => 
    (contract.sku === 99999 || contract.sku === -1)
  );

  const completedOrVoidContracts = data.filter(contract => 
    contract.status === 'completed' || 
    contract.status === 'void'
  );

  // Group sell contracts with draft contracts by lotID
  const sellContractsWithDrafts = [...sellContracts, ...draftContracts];

  return (
    <>
      <UpdateContractModal 
        open={open} 
        toggle={handleModalClose} 
        isUpdate={isUpdate} 
        selected={selected}
      />
      
      <Group position="right" mb="md">
        <Group>
          <Button
            variant={viewMode === 'cards' ? 'filled' : 'outline'}
            onClick={() => setViewMode('cards')}
            compact
          >
            <FormattedMessage id="view.cards" defaultMessage="Cards" />
          </Button>
          <Button
            variant={viewMode === 'table' ? 'filled' : 'outline'}
            onClick={() => setViewMode('table')}
            compact
          >
            <FormattedMessage id="view.table" defaultMessage="Table" />
          </Button>
        </Group>
      </Group>

      {loading ? (
        <Center style={{ height: 200 }}>
          <Loader size="lg" />
        </Center>
      ) : (
        <Tabs defaultValue="buy">
          <Tabs.List mb="md">
            <Tabs.Tab 
              value="buy" 
              icon={<IconShoppingCart size="0.8rem" />}
            >
              <FormattedMessage id="contracts.buy" defaultMessage="Buy in Negotiation" /> ({buyContracts.length})
            </Tabs.Tab>
            <Tabs.Tab 
              value="sell" 
              icon={<IconPackage size="0.8rem" />}
            >
              <FormattedMessage id="contracts.sell" defaultMessage="Sell in Negotiation" /> ({sellContracts.length + draftContracts.length})
            </Tabs.Tab>
            <Tabs.Tab 
              value="completed" 
              icon={<IconCheck size="0.8rem" />}
            >
              <FormattedMessage id="contracts.completed" defaultMessage="Completed & Void" /> ({completedOrVoidContracts.length})
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="buy">
            {viewMode === 'table' ? (
              <ContractTable 
                data={buyContracts} 
                onActionSelect={handleActionSelect} 
              />
            ) : (
              <ContractCards 
                data={buyContracts} 
                onActionSelect={handleActionSelect} 
                isGrouped={false}
                groupField="productSKU"
              />
            )}
          </Tabs.Panel>

          <Tabs.Panel value="sell">
            {viewMode === 'table' ? (
              <ContractTable 
                data={sellContractsWithDrafts} 
                onActionSelect={handleActionSelect} 
              />
            ) : (
              <ContractCards 
                data={sellContractsWithDrafts} 
                onActionSelect={handleActionSelect}
                isGrouped={true}
                groupField="productSKU"
              />
            )}
          </Tabs.Panel>

          <Tabs.Panel value="completed">
            {viewMode === 'table' ? (
              <ContractTable 
                data={completedOrVoidContracts} 
                onActionSelect={handleActionSelect} 
              />
            ) : (
              <ContractCards 
                data={completedOrVoidContracts} 
                onActionSelect={handleActionSelect}
                isGrouped={true}
                groupField="productSKU"
              />
            )}
          </Tabs.Panel>
        </Tabs>
      )}
    </>
  );
}